import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import _ from "lodash";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { MessageSquare, Mail, Clock } from "lucide-react";

import { db } from "../../firebase";
import VenueSelect from "../../components/VenueSelect";
import Message from "../../components/Message";

function Threads() {
  const [threads, setThreads] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    async function fetchThreads() {
      if (!isMounted) return;
      setError("");
      setLoading(true);

      if (!params.venueId) {
        setThreads([]);
        setLoading(false);
        return;
      }

      try {
        const q = query(collection(db, "conversations"), where("venueId", "==", params.venueId));
        const querySnapshot = await getDocs(q);
        if (!isMounted) return;

        const threadsData = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setSelectedVenue(params.venueId);

        const sortedThreads = _.orderBy(
          threadsData,
          [
            (thread) => {
              try {
                return thread.messages?.[0]?.date?.toDate?.() || new Date(0);
              } catch (e) {
                return new Date(0);
              }
            },
          ],
          ["desc"],
        );

        setThreads(sortedThreads);
      } catch (err) {
        console.error("Error fetching threads:", err);
        if (isMounted) setError("Er is een fout opgetreden bij het ophalen van de berichten.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchThreads();
    return () => {
      isMounted = false;
    };
  }, [params.venueId]);

  const handleVenueChange = (value, doc) => {
    if (!doc) {
      setSelectedVenue(null);
      navigate(`/threads`);
      return;
    }
    setSelectedVenue(value);
    navigate(`/threads/${value}`);
  };

  const venueSelector = (
    <VenueSelect activeVenueId={selectedVenue || params.venueId} onChange={handleVenueChange} showNewButton={false} isStandAlone={true} />
  );

  if (!params.venueId) {
    return (
      <div>
        {venueSelector}
        {error && <Message text={error} />}
      </div>
    );
  }

  return (
    <div>
      {venueSelector}
      {error && <Message text={error} />}

      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="skeleton w-10 h-10 rounded-full" />
                <div className="flex-1">
                  <div className="skeleton h-4 w-32 mb-2 rounded" />
                  <div className="skeleton h-3 w-24 rounded" />
                </div>
              </div>
              <div className="skeleton h-3 w-full mb-2 rounded" />
              <div className="skeleton h-3 w-3/4 rounded" />
            </div>
          ))}
        </div>
      )}

      {!loading && threads.length === 0 && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-bg-sunken flex items-center justify-center">
            <MessageSquare size={28} strokeWidth={1.5} className="text-text-tertiary" />
          </div>
          <h3 className="text-lg font-heading font-semibold mb-2">Geen berichten</h3>
          <p className="text-sm text-text-secondary max-w-sm mx-auto">
            Er zijn nog geen berichten voor deze locatie. Zodra bezoekers contact opnemen, verschijnen hun berichten hier.
          </p>
        </div>
      )}

      {!loading && threads.length > 0 && (
        <div className="space-y-3">
          {threads.map((thread) => {
            const firstMessage = thread.messages?.[0] || {};
            const isReplied = thread.messages && thread.messages.length > 1;

            let formattedDate = "";
            try {
              if (firstMessage.date?.toDate) {
                formattedDate = format(firstMessage.date.toDate(), "d MMMM yyyy HH:mm", { locale: nl });
              }
            } catch (e) {
              /* ignore date format errors */
            }

            return (
              <div key={thread.id} className="card p-5 hover:shadow-sm transition-shadow duration-fast group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent-subtle flex items-center justify-center shrink-0">
                    <span className="text-sm font-semibold text-accent">{(thread.prospectName || "?")[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-base font-semibold text-text-primary truncate">{thread.prospectName || "Onbekende naam"}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        {thread.type === "party_request" ? (
                          <span className="badge bg-accent-subtle text-accent">Feestje zoeker</span>
                        ) : (
                          <span className="badge bg-info-subtle text-info">Direct</span>
                        )}
                        {isReplied ? (
                          <span className="badge bg-success-subtle text-success">Beantwoord</span>
                        ) : (
                          <span className="badge bg-warning-subtle text-warning">Nieuw</span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-text-secondary line-clamp-2 mb-2">{firstMessage.body}</p>
                    <div className="flex items-center gap-4 text-xs text-text-tertiary">
                      {formattedDate && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {formattedDate}
                        </span>
                      )}
                      <a
                        href={`mailto:${thread.prospectEmail}`}
                        className="flex items-center gap-1 text-accent hover:underline underline-offset-2"
                      >
                        <Mail size={12} /> {thread.prospectEmail}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Threads;
